import React, { Fragment, useState } from "react";
import { Badge, Collapse, Table, Tag, message } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import moment from "moment";
import * as Sentry from "@sentry/browser";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  formatter,
  generateDisplayErrorMessage,
  getStateColor,
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import { tabPaneStyle } from "../helpers/constants";
import {
  APIError,
  FinancialInstitution,
  FixedIncomeRedemption,
  TransitionToNextState,
  SelectOptionsInterface,
  FixedIncomeRequestReason,
} from "../helpers/shared-interfaces";
import ModalCreator from "./ModalCreator";
import { LoanRequestReasonUI } from "../pages/ViewLoanRequest";
import {
  MOVE_FIXED_INCOME_REDEMPTION_TO_STATE,
} from "../gql/fixed-income-requests";
import { NavLink } from "react-router-dom";

interface FixedIncomeRedemptionsTableProps {
  redemptions: FixedIncomeRedemption[];
  onRedemptionUpdate: () => void;
  financialInstitutions: FinancialInstitution[];
}

export const FixedIncomeRedemptionsTable: React.FC<
  FixedIncomeRedemptionsTableProps
> = ({ redemptions, onRedemptionUpdate, financialInstitutions }) => {
  const [selectedRedemption, setSelectedRedemption] =
    useState<FixedIncomeRedemption | null>(null);
  const [showStateModal, setShowStateModal] = useState(false);
  const [selectedTransitionState, setSelectedTransitionState] = useState("");
  const [isStateLoading, setIsStateLoading] = useState(false);
  const [institutionOptions, setInstitutionOptions] = useState<
    FinancialInstitution[]
  >([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | undefined
  >();
  const [fixedIncomeRequestReasons, setFixedIncomeRequestReasons] = useState<
    FixedIncomeRequestReason[]
  >([]);
  const [selectedReasonId, setSelectedReasonId] = useState<
    string | undefined
  >();

  const stateReasonReference = React.useRef<FormInstance>(null);

  const reasons: SelectOptionsInterface[] = fixedIncomeRequestReasons?.map(
    (reason) => {
      return { id: reason.id, name: reason.description || "" };
    },
  );

  // const handleStateTransition = async (
  //   redemption: FixedIncomeRedemption,
  //   state: string,
  // ) => {
  //   if (isStateLoading) {
  //     return;
  //   }

  //   interface Response {
  //     fixedIncomeRequestReasons: FixedIncomeRequestReason[];
  //     errors: APIError[];
  //   }

  //   setSelectedRedemption(redemption);
  //   setSelectedTransitionState(state);
  //   setIsStateLoading(true);

  //   try {
  //     request({
  //       query: GET_FIXED_INCOME_REQUEST_REASONS,
  //       variables: { state },
  //     })
  //       .then(({ data }: AxiosResponse<{ data: Response }>) => {
  //         setFixedIncomeRequestReasons(data.data.fixedIncomeRequestReasons);
  //         setShowStateModal(true);
  //       })
  //       .finally(() => {
  //         setIsStateLoading(false);
  //       });
  //   } catch (error) {
  //     message.error("An error occurred, please try again later");
  //     Sentry.captureException(error);
  //     setIsStateLoading(false);
  //   }
  // };

  const toggleStateModal = () => {
    setShowStateModal(!showStateModal);
    if (showStateModal) {
      setSelectedRedemption(null);
      setSelectedTransitionState("");
      setInstitutionOptions([]);
      setSelectedPaymentMethod(undefined);
      setFixedIncomeRequestReasons([]);
      setSelectedReasonId(undefined);
    }
  };

  const handlePaymentMethodSelect = (paymentMethod: string): void => {
    let filteredInstitutions: FinancialInstitution[] = [];

    if (paymentMethod === "mobile_money") {
      filteredInstitutions = financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else if (
      paymentMethod === "bank_transfer" ||
      paymentMethod === "cheque"
    ) {
      filteredInstitutions = financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );
    } else {
      filteredInstitutions = financialInstitutions;
    }

    setInstitutionOptions(filteredInstitutions);
    setSelectedPaymentMethod(paymentMethod);
  };

  const handleReasonSelect = (reasonId: string): void => {
    setSelectedReasonId(reasonId);
  };

  const saveStateTransition = async (): Promise<void> => {
    if (!stateReasonReference.current || !selectedRedemption) return;

    const selectedTransition =
      selectedRedemption.canTransitionToNextStates?.find(
        (t: TransitionToNextState) => t.state === selectedTransitionState,
      );

    if (!selectedTransition) return;

    const requiresReason = selectedTransition?.reason_required || false;
    const fieldsToBeValidated = ["comment"];

    if (requiresReason) {
      fieldsToBeValidated.push("fixedIncomeRequestReasonId");
    }

    let data;

    try {
      data = await stateReasonReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    setIsStateLoading(true);

    try {
      await moveRedemptionToState(data, selectedTransition);
    } catch (error) {
      Sentry.captureException(error);
      message.error(`Failed to process state transition - ${generateDisplayErrorMessage(error)}`);
    } finally {
      if (stateReasonReference.current) {
        stateReasonReference.current.resetFields();
      }
      setIsStateLoading(false);
      setShowStateModal(false);
      setSelectedRedemption(null);
      setSelectedTransitionState("");
      setInstitutionOptions([]);
      setSelectedPaymentMethod(undefined);
      setFixedIncomeRequestReasons([]);
      setSelectedReasonId(undefined);
    }
  };

  const moveRedemptionToState = async (
    data: any,
    transition: TransitionToNextState,
  ): Promise<void> => {
    interface Response {
      fixedIncomeRedemption: FixedIncomeRedemption;
      errors: APIError[];
    }

    const variables: any = {
      fixedIncomeRedemptionId: selectedRedemption?.id,
      comment: data.comment,
      state: selectedTransitionState,
      event: transition.event,
    };

    if (transition?.reason_required && data.fixedIncomeRequestReasonId) {
      variables.fixedIncomeRequestReasonId = data.fixedIncomeRequestReasonId;
    }

    const response: AxiosResponse = await request({
      query: MOVE_FIXED_INCOME_REDEMPTION_TO_STATE,
      variables,
    });

    const { err, mutation } = responseHandler<Response>(
      response,
      "moveFixedIncomeRedemptionToState",
    );

    const error = err || mutation.errors;

    if (mutation.fixedIncomeRedemption) {
      const newState = mutation.fixedIncomeRedemption.state;
      const stateMessages: Record<string, string> = {
        approved: "Redemption has been approved",
        processing: "Redemption is being processed",
        processed: "Redemption has been processed",
        payment_delayed: "Redemption payment has been delayed",
        paid: "Redemption has been paid",
        failed: "Redemption has failed",
        cancelled: "Redemption has been cancelled",
      };

      const successMessage = newState
        ? stateMessages[newState] ||
          `Redemption state updated to ${formatSnakeCase(newState)}`
        : "Redemption state updated successfully";

      message.success(successMessage);
      onRedemptionUpdate();
    } else if (error) {
      message.error(generateDisplayErrorMessage(error));
    } else {
      throw new Error(generateDisplayErrorMessage(error));
    }
  };

  // const renderStateButtons = (redemption: FixedIncomeRedemption) => {
  //   const transitions = redemption.canTransitionToNextStates || [];

  //   if (transitions.length === 0) return null;

  //   return (
  //     <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
  //       {transitions.map((transition: TransitionToNextState, index: number) => (
  //         <Button
  //           key={index}
  //           size="small"
  //           onClick={() => handleStateTransition(redemption, transition.state)}
  //           className={getStateButtonClass(transition.state)}
  //           disabled={isStateLoading}
  //         >
  //           {generateStateButtonText(transition.event)}
  //         </Button>
  //       ))}
  //     </div>
  //   );
  // };

  return (
    <Fragment>
      <Collapse style={{ marginTop: 16 }}>
        <Collapse.Panel
          key="redemptions"
          header={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>
                Withdrawals{" "}
                <Badge count={redemptions.length} style={tabPaneStyle} />
              </span>
            </div>
          }
        >
          <Table
            dataSource={redemptions}
            rowKey="id"
            pagination={false}
            size="small"
            summary={(pageData) => {
              const totalAmount = pageData.reduce(
                (sum, redemption) => sum + (redemption.amount || 0),
                0,
              );
              const totalInterest = pageData.reduce(
                (sum, redemption) => sum + (redemption.interestEarned || 0),
                0,
              );
              const totalPenalty = pageData.reduce(
                (sum, redemption) => sum + (redemption.penalty || 0),
                0,
              );

              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={1}>
                      <b>Total</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <b>{formatter.format(totalAmount)}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <b>{formatter.format(totalInterest)}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <b>{formatter.format(totalPenalty)}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} colSpan={5} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          >
            <Table.Column
              title="View"
              key="view"
              width="8%"
              align="center"
              render={(_, redemption: FixedIncomeRedemption) => (
                <NavLink to={`/fixed-income-redemptions/${redemption.id}`}>
                  <EyeOutlined style={{ fontSize: 16 }} />
                </NavLink>
              )}
            />
            <Table.Column
              title="Redemption Date"
              dataIndex="createdAt"
              key="createdAt"
              width="10%"
              render={(date: string) =>
                date
                  ? moment(date, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY")
                  : "N/A"
              }
            />
            <Table.Column
              title="Amount"
              dataIndex="amount"
              key="amount"
              width="10%"
              render={(amount: number) =>
                amount ? formatter.format(amount) : "N/A"
              }
            />
            <Table.Column
              title="Interest Earned"
              dataIndex="interestEarned"
              key="interestEarned"
              width="10%"
              render={(interest: number) =>
                interest ? formatter.format(interest) : "GHS 0.00"
              }
            />
            <Table.Column
              title="Penalty"
              dataIndex="penalty"
              key="penalty"
              width="10%"
              render={(penalty: number) =>
                penalty ? formatter.format(penalty) : "GHS 0.00"
              }
            />
            <Table.Column
              title="State"
              dataIndex="state"
              key="state"
              width="10%"
              render={(state: string) => (
                <Tag
                  color={getStateColor(state)}
                  style={{ textTransform: "capitalize" }}
                >
                  {state ? formatSnakeCase(state) : "N/A"}
                </Tag>
              )}
            />
            <Table.Column
              title="Roll Over"
              dataIndex="rollOver"
              key="rollOver"
              width="8%"
              render={(rollOver: boolean) => (
                <Tag color={rollOver ? "green" : "red"}>
                  {rollOver ? "Yes" : "No"}
                </Tag>
              )}
            />
            <Table.Column
              title="Assigned To"
              dataIndex="assignedTo"
              key="assignedTo"
              width="12%"
              render={(assignedTo: any) => assignedTo?.name || "N/A"}
            />
            <Table.Column
              title="Rolled Over Request"
              dataIndex="rolledOverFixedIncomeRequest"
              key="rolledOverFixedIncomeRequest"
              width="12%"
              align="center"
              render={(rolledOverRequest: any) =>
                rolledOverRequest?.id ? (
                  <NavLink
                    to={`/fixed-income-requests/${rolledOverRequest.id}`}
                  >
                    <EyeOutlined style={{ fontSize: 16 }} />
                  </NavLink>
                ) : (
                  "N/A"
                )
              }
            />
          </Table>
        </Collapse.Panel>
      </Collapse>

      {showStateModal &&
        selectedRedemption &&
        ModalCreator({
          title: `Update Redemption State - ${capitalizeFirstLetter(
            formatSnakeCase(selectedTransitionState),
          )}`,
          okType: "primary",
          okText: "Submit",
          showModal: showStateModal,
          onOkay: saveStateTransition,
          toggleModal: toggleStateModal,
          isLoading: isStateLoading,
          okayButtonProps: {
            disabled: isStateLoading,
          },
          inscription: LoanRequestReasonUI({
            reference: stateReasonReference,
            selectedTransitionState,
            availableTransitionStates:
              selectedRedemption.canTransitionToNextStates,
            isLoading: isStateLoading,
            commentRequired: true,
            financialInstitutions: financialInstitutions,
            handlePaymentMethodSelect: handlePaymentMethodSelect,
            institutionOptions: institutionOptions,
            selectedPaymentMethod: selectedPaymentMethod,
            reasons,
            handleReasonSelect: handleReasonSelect,
          }),
        })}
    </Fragment>
  );
};
